<template>
  <div class="services">
    <div
      v-if="loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div v-if="!loading">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          class="pt-0"
        >
          <h1>
            {{ $t('services_overview.txt.services') }}
          </h1>
        </v-col>
        <v-col
          v-if="!!sortItems.length"
          cols="6"
          sm="4"
          class="pt-2 w-25"
        >
          <span class="sort-label">Filter By:</span>
          <v-select
            v-model="defaultSelected"
            class="basic"
            background-color="pale-three"
            color="slate"
            :append-icon="'mdi-chevron-down'"
            :items="sortItems"
            :menu-props="{ offsetY: true }"
            dense
            filled
            rounded
            return-object
            @change="filterBySubscriptionLevel"
          />
        </v-col>
        <v-col
          cols="6"
          :sm="sortItems.length?2:6"
          class="pt-0"
        >
          <div class="totals float-sm-right pl-8 pl-sm-0">
            <div
              v-if="monthlyTotalCost>0 || yearlyTotalCost>0"
              class="label"
            >
              {{ $t('services_overview.txt.total_cost') }}
            </div>
            <div>
              <span
                v-if="monthlyTotalCost>0"
                class="total"
              >{{ totalMonthPrice | formatPrice(currency.symbol) }} </span>
              <span v-if="monthlyTotalCost>0">{{ $t('services_overview.txt.month') }}</span>
              <span v-if="monthlyTotalCost>0 && yearlyTotalCost>0"> & </span>
              <span
                v-if="yearlyTotalCost>0"
                class="total"
              >{{ totalAnnualPrice | formatPrice(currency.symbol) }} </span>
              <span v-if="yearlyTotalCost>0">{{ $t('services_overview.txt.year') }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          v-for="service in filteredActiveServices"
          :key="service.id"
          cols="12"
          md="6"
          xl="4"
        >
          <ServicesCard
            :service="service"
            :service-annual-price="service.priceServiceAnnual"
            :service-total-annual-price="service.totalAnnual"
            :service-month-price="service.priceServiceMonth"
            :service-total-month-price="service.totalMonth"
          />
        </v-col>
        <v-col
          v-if="filteredActiveServices.length==0"
          class="no-services"
        >
          <div>
            <p class="no-servicesText">
              {{ $t('services_overview.txt.no_services_available') }}
            </p>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import prismicService from '@/helpers/prismicService'
import { mapState, mapActions } from 'vuex'
import ServicesCard from '@/components/ServicesCard.vue'
import { ServicesMixin } from '@/mixins/ServicesMixin.js'
import { formatRelationsData } from '@/helpers/filters'
import visibilityHelper from '@/helpers/visibilityHelper'
import { ProductType } from '@/helpers/enums'

export default {
  name: 'ServiceOverview',
  components: {
    ServicesCard
  },
  mixins: [ServicesMixin],
  data() {
    return {
      services: [],
      allServices: [],
      filteredActiveServices: [],
      activeServices: [],
      monthlyTotalCost: 0,
      yearlyTotalCost: 0,
      loading: false,
      defaultSelected: 'All',
      serviceType: ''
    }
  },
  computed: {
    ...mapState('account', [
      'currency',
      'featureFlags',
      'accountRelations',
      'synchronizedTruckVisibility',
      'synchronizedTruckVisibilityLoading'
    ]),
    orderedServices() {
      const array = this.services
      array.sort((x, y) => {
        if (x.name < y.name) return -1
        if (x.name > y.name) return 1
        return 0
      })
      return 0
    },
    sortItems() {
      const formatedRelationsData = formatRelationsData(this.accountRelations)
      if (!formatedRelationsData.length || formatedRelationsData.length == 1) {
        return []
      }

      formatedRelationsData.sort((a, b) => (a.sortHint > b.sortHint ? -1 : 1))

      return [{ key: 'All', text: 'All' }, ...formatedRelationsData]
    }
  },
  watch: {
    currency(newValue, oldValue) {
      if (
        newValue &&
        newValue.code &&
        (!oldValue || oldValue.code !== newValue.code)
      ) {
        this.monthlyTotalCost = 0
        this.yearlyTotalCost = 0
        this.getServices()
      }
    },
    $route() {
      if (
        this.serviceType &&
        this.$route.params.type &&
        this.$route.params.type != this.serviceType
      ) {
        this.loading = true
        this.getServices()
      }
    }
  },
  async created() {
    this.loading = true
    this.getVehicles()
    this.updateSynchronizedVisibilityNotification()
    await this.getServices()
  },
  methods: {
    ...mapActions('account', ['setSynchronizedVisibilityLoading']),
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('trucks', ['getVehicles', 'getTrucksPerService']),
    ...mapActions('products', ['getServicesProducts', 'getCompanyService']),

    filterBySubscriptionLevel(targetSubscriptionLevel) {
      if (targetSubscriptionLevel.key === 'All') {
        return (this.filteredActiveServices = this.services)
      }
      const filteredActiveServices = this.services.filter(item =>
        targetSubscriptionLevel.roles.includes(item.subscriptionLevel__c)
      )
      this.filteredActiveServices = filteredActiveServices
    },
    async getServices() {
      this.serviceType = this.$route.params.type || ProductType.Service
      // Usar store com cache para evitar chamadas redundantes
      const services = await this.getServicesProducts()
      this.allServices = services.filter(
        service => service.type__c === this.serviceType
      )
      return Promise.resolve().then(() => {
          this.allServices.sort((x, y) => {
            if (
              x.category === 'Base Products' &&
              y.category !== 'Base Products'
            ) {
              return -1
            } else if (
              x.category !== 'Base Products' &&
              y.category === 'Base Products'
            ) {
              return 1
            }

            if (x.name < y.name) return -1
            if (x.name > y.name) return 1
            return 0
          })
        })
        .then(async () => {
          this.activeServices = (await this.getTrucksPerService()) || []
          return this.activeServices
        })
        .then(activeServices => {
          return prismicService
            .getProductCatalog()
            .then(catalog => {
              // reset global totals
              this.monthlyTotalCost = 0
              this.yearlyTotalCost = 0

              this.allServices.forEach(serv => {
                if (
                  !visibilityHelper.hasRelationForService(
                    serv,
                    this.accountRelations
                  )
                ) {
                  return true
                }

                //extra properties init
                serv.subscribableMontlhy = false
                serv.showMonthlyPrice = !!this.currency.code
                serv.subscribableAnnual = false
                serv.showAnnualPrice = !!this.currency.code
                serv.productRatePlans.forEach(product => {
                  //only monthly or yearly plans
                  if (
                    product.billingPeriod__c != 'Annual' &&
                    product.billingPeriod__c != 'Monthly'
                  ) {
                    return true
                  }
                  product.productRatePlanCharges.forEach(bp => {
                    if (bp.billingPeriod === 'Annual') {
                      const annualPrice =
                        bp.pricing.find(
                          pr => pr.currency === this.currency.code
                        ) || 0
                      serv.priceServiceAnnual = annualPrice.price
                      serv.subscribableAnnual =
                        product.isAllowedToSubscribe__c == 'True'
                      serv.productPlanChargesIdYearly = bp.id
                      serv.productRatePlanIdYearly = product.id
                    } else if (bp.billingPeriod === 'Month') {
                      const monthPrice =
                        (product.isAllowedToSubscribe__c == 'True' &&
                          bp.pricing.find(
                            pr => pr.currency === this.currency.code
                          )) ||
                        0
                      serv.priceServiceMonth = monthPrice.price
                      serv.subscribableMontlhy =
                        product.isAllowedToSubscribe__c == 'True'
                      serv.productPlanChargesIdMonthly = bp.id
                      serv.productRatePlanIdMonthly = product.id
                    }
                  })
                })
                serv.priceServiceAnnual = serv.priceServiceAnnual || 0
                serv.priceServiceMonth = serv.priceServiceMonth || 0

                activeServices.forEach(actServ => {
                  if (
                    serv.id === actServ.productID // || serv.packID__c == actServ.packId
                  ) {
                    serv.activeServices = actServ.activeTrucks
                    serv.totalAnnual = actServ.totals.Annual
                    serv.totalMonth = actServ.totals.Month
                    serv.customerServiceSubscribed =
                      serv.subscriptionLevel__c == 'Company' &&
                      serv.packID__c == actServ.packId
                    serv.status = actServ.status
                  }
                })

                /// hide prices for unsubscribable rateplans
                if (!serv.subscribableMontlhy) {
                  serv.showMonthlyPrice = false
                }

                if (!serv.subscribableAnnual) {
                  serv.showAnnualPrice = false
                }
                serv.hasActivePlans =
                  serv.subscribableMontlhy || serv.subscribableAnnual

                serv.activeServices = serv.activeServices || 0
                catalog.forEach(catalogProduct => {
                  if (catalogProduct.externalKey === serv.sku) {
                    serv.name = catalogProduct.title
                    serv.iconUrl = catalogProduct.iconActiveUrl
                    serv.description = catalogProduct.description
                  }
                })
                return serv
              })
              this.services = this.allServices.filter(
                serv =>
                  serv.hasActivePlans &&
                  (!serv.isLifetime || this.featureFlags.useEssentials)
              )
              this.filteredActiveServices = this.services

              this.loading = false
            })
            .then(this.calculateTotals)
            .then(async () => {
              // Usar store com cache para evitar chamadas redundantes
              const companyService = await this.getCompanyService()
              if (companyService) {
                this.addCompanyTotals(companyService)
              }
            })
        })
    },
    updateSynchronizedVisibilityNotification() {
      if (
        (!this.synchronizedTruckVisibility.addedVisibility ||
          !this.synchronizedTruckVisibility.revokedVisibility) &&
        !this.synchronizedTruckVisibility.notified
      ) {
        this.synchronizedTruckVisibility.notified = true
        this.setSnackbar({
          showing: true,
          type: 'warning',
          text: 'Not all vehicles were able to be synchronized'
        })
      }
    }
  }
}
</script>

<style lang="scss">
.services {
  .no-services {
    // font-size: 0.875rem;
    color: var(--v-slate-base);
    text-align: center;
  }
  .no-servicesText {
    padding-top: 2rem;
    font-size: 3rem;
  }
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .basic {
    display: inline-flex;
    max-width: 250px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .totals {
    .label {
      font-size: 0.75rem;
      font-weight: bold;
      text-transform: uppercase;
      color: var(--v-bluegrey-base);
    }
    .total {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--v-slate-base);
      span {
        font-size: 0.875rem;
      }
    }
  }
  .sort-label {
    font-weight: bold;
    text-align: right;
    color: var(--v-slate-base);
    margin-right: 8px;
  }
}
</style>